import { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Plus,
  Pencil,
  Trash2,
  Tag,
  ChevronDown,
  ChevronUp,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useKeywords } from '@/lib/hooks/use-keywords';
import { useKeywordSynonyms } from '@/lib/hooks/use-keyword-synonyms';
import { useAdminPreview } from '@/lib/admin-preview-context';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@/components/ui/collapsible';

const SYNONYMS_ENABLED = false; // not (yet) supported so hiding UI

export default function AdminKeywords() {
  const { isPreviewMode } = useAdminPreview();
  const roleScope = isPreviewMode ? 'hoofdhartuser' : null;
  const [refreshCount, setRefreshCount] = useState<number>(0);

  // In normal mode, use the hook. In preview mode, fetch with role_scope filter
  const { keywords: hookKeywords, loading: hookLoading } = useKeywords([refreshCount]);
  const [hnhKeywords, setHnhKeywords] = useState<any[]>([]);
  const [hnhLoading, setHnhLoading] = useState(false);

  useEffect(() => {
    if (!isPreviewMode) return;
    setHnhLoading(true);
    supabase.from('keywords').select('*').eq('role_scope', 'hoofdhartuser').order('name')
      .then(({ data }) => { setHnhKeywords(data ?? []); setHnhLoading(false); });
  }, [isPreviewMode, refreshCount]);

  const keywords = isPreviewMode ? hnhKeywords : hookKeywords;
  const loading = isPreviewMode ? hnhLoading : hookLoading;
  const { addSynonym, removeSynonym } = useKeywordSynonyms();
  const [editingKeyword, setEditingKeyword] = useState(null);
  const [savingKeyword, setSavingKeyword] = useState(false);
  const [expandedKeywords, setExpandedKeywords] = useState<string[]>([]);
  const [newSynonym, setNewSynonym] = useState('');
  const [addingSynonymFor, setAddingSynonymFor] = useState<string | null>(null);
  const [savingSynonym, setSavingSynonym] = useState(false);

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setSavingKeyword(true);

    const formData = new FormData(e.currentTarget);
    const name = formData.get('name') as string;

    try {
      if (editingKeyword) {
        await supabase
          .from('keywords')
          .update({ name })
          .eq('id', editingKeyword.id);
      } else {
        await supabase.from('keywords').insert([{ name, role_scope: roleScope }]);
      }
      setEditingKeyword(null);
      setRefreshCount((prev) => prev + 1); // trigger refresh
    } catch (err) {
      console.error('Error saving keyword:', err);
    } finally {
      setSavingKeyword(false);
    }
  }

  async function deleteKeyword(id: string) {
    if (confirm('Are you sure you want to delete this keyword?')) {
      await supabase.from('keywords').delete().eq('id', id);
      setRefreshCount((prev) => prev + 1); // trigger refresh
    }
  }

  const toggleKeywordExpansion = (keywordId: string) => {
    setExpandedKeywords((prev) =>
      prev.includes(keywordId)
        ? prev.filter((id) => id !== keywordId)
        : [...prev, keywordId],
    );
  };

  const handleAddSynonym = async (keywordId: string) => {
    if (!newSynonym.trim() || !keywordId) return;

    setSavingSynonym(true);
    try {
      await addSynonym(keywordId, newSynonym.trim().toLowerCase());
      setNewSynonym('');
      setAddingSynonymFor(null);
      setRefreshCount((prev) => prev + 1); // trigger refresh
    } catch (error) {
      console.error('Error adding synonym:', error);
    } finally {
      setSavingSynonym(false);
    }
  };

  const handleRemoveSynonym = async (synonymId: string) => {
    if (confirm('Are you sure you want to delete this synonym?')) {
      await removeSynonym(synonymId);
      setRefreshCount((prev) => prev + 1); // trigger refresh
    }
  };

  // Debug: Log keywords when they change
  useEffect(() => {
    if (!loading) {
      console.log('Keywords in component:', keywords);
      console.log(
        'Extra keywords:',
        keywords.filter((k) =>
          ['loslaten', 'inzicht', 'uitzoomen'].includes(k.name.toLowerCase()),
        ),
      );
    }
  }, [keywords, loading]);

  return (
    <div className="space-y-8">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <h1 className="text-2xl font-semibold">{isPreviewMode ? 'HnH Keywords' : 'Keywords'}</h1>
          <Dialog
            open={!!editingKeyword}
            onOpenChange={() => setEditingKeyword(null)}
          >
            <DialogTrigger asChild>
              {!editingKeyword && (
                <Button className="bg-green-600 hover:bg-green-700">
                  <Plus className="h-4 w-4 mr-2" />
                  Add Keyword
                </Button>
              )}
            </DialogTrigger>
            <DialogContent className="sm:max-w-[425px]">
              <DialogHeader>
                <DialogTitle>
                  {editingKeyword ? 'Edit Keyword' : 'Add New Keyword'}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="name">Keyword Name</Label>
                  <Input
                    id="name"
                    name="name"
                    defaultValue={editingKeyword?.name}
                    required
                  />
                </div>

                <Button
                  type="submit"
                  className="w-full"
                  disabled={savingKeyword}
                >
                  {savingKeyword
                    ? 'Saving...'
                    : editingKeyword
                      ? 'Update Keyword'
                      : 'Create Keyword'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {loading ? (
        <p>Loading keywords...</p>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {keywords.map((keyword) => (
            <Collapsible
              key={keyword.id}
              open={expandedKeywords.includes(keyword.id)}
            >
              <Card className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <h3 className="text-lg font-semibold">{keyword.name}</h3>
                  </div>
                  <div className="flex gap-2">
                    <CollapsibleTrigger asChild>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => toggleKeywordExpansion(keyword.id)}
                        className={`${!SYNONYMS_ENABLED ? 'hidden' : ''}`}
                      >
                        {expandedKeywords.includes(keyword.id) ? (
                          <ChevronUp className="h-4 w-4" />
                        ) : (
                          <ChevronDown className="h-4 w-4" />
                        )}
                      </Button>
                    </CollapsibleTrigger>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setEditingKeyword(keyword)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="destructive"
                      size="sm"
                      onClick={() => deleteKeyword(keyword.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>

                <CollapsibleContent>
                  <div className="mt-4 pt-4 border-t">
                    <h4 className="font-medium mb-2">Synonyms</h4>

                    {keyword.synonyms && keyword.synonyms.length > 0 ? (
                      <div className="space-y-2 mb-4">
                        {keyword.synonyms.map((synonym, index) => {
                          // Find the synonym object with the matching text to get its ID
                          const synonymObj = keyword.synonymsData?.find(
                            (s) => s.synonym === synonym,
                          );
                          return (
                            <div
                              key={index}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                            >
                              <span>{synonym}</span>
                              {synonymObj && (
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() =>
                                    handleRemoveSynonym(synonymObj.id)
                                  }
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              )}
                            </div>
                          );
                        })}
                      </div>
                    ) : (
                      <p className="text-sm text-gray-500 mb-4">
                        No synonyms added yet.
                      </p>
                    )}

                    <div className="flex gap-2">
                      {addingSynonymFor === keyword.id ? (
                        <div className="flex w-full gap-2">
                          <Input
                            value={newSynonym}
                            onChange={(e) => setNewSynonym(e.target.value)}
                            placeholder="Enter new synonym"
                            className="flex-1"
                          />
                          <Button
                            onClick={() => handleAddSynonym(keyword.id)}
                            disabled={savingSynonym || !newSynonym.trim()}
                            className="bg-green-600 hover:bg-green-700"
                          >
                            {savingSynonym ? 'Adding...' : 'Add'}
                          </Button>
                          <Button
                            variant="outline"
                            onClick={() => {
                              setAddingSynonymFor(null);
                              setNewSynonym('');
                            }}
                          >
                            Cancel
                          </Button>
                        </div>
                      ) : (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => setAddingSynonymFor(keyword.id)}
                          className="w-full"
                        >
                          <Plus className="h-4 w-4 mr-2" />
                          Add Synonym
                        </Button>
                      )}
                    </div>
                  </div>
                </CollapsibleContent>
              </Card>
            </Collapsible>
          ))}
        </div>
      )}
    </div>
  );
}
